// EditorToolbar - S47.1 / S47.2 / S48.1.
// Toolbar con botones draggable. Cada boton inicia un drag con MIME custom
// (DND_MIME) y payload JSON { type, bindKey, wMm, hMm } leido desde
// dynamicFields.
//
// S48.1: acepta prop `allowedFields` (array de keys de DYNAMIC_FIELDS) que
// filtra los botones visibles. Si null/undefined, muestra todos (modo libre).
import { useEffect, useMemo } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import { DND_MIME } from '../constants';
import { DYNAMIC_FIELDS } from '../dynamicFields';

const CSS_ID = 'xl-et-toolbar-css';
const CSS = `
.xl-et-toolbar {
  display: flex; flex-direction: column; gap: 8px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  padding: 12px;
  box-shadow: var(--xl-shadow-sm);
  font-family: var(--xl-font-sans);
}
.xl-et-toolbar__hd {
  font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--xl-color-text-muted);
  padding: 4px 4px 8px;
}
.xl-et-toolbar__btn {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; border-radius: var(--xl-radius-md);
  border: 1px dashed var(--xl-color-border);
  background: var(--xl-color-surface-alt);
  color: var(--xl-color-text);
  font: 500 13px/1 var(--xl-font-sans);
  cursor: grab; user-select: none;
  transition: border-color 120ms cubic-bezier(0,0,0.2,1),
              background   120ms cubic-bezier(0,0,0.2,1);
}
.xl-et-toolbar__btn:hover {
  border-color: rgba(46, 55, 69, 0.25);
  background: var(--xl-color-surface);
}
.xl-et-toolbar__btn:active { cursor: grabbing }
.xl-et-toolbar__btn-ico {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px;
  font-size: 14px; line-height: 1;
  color: var(--xl-color-text-secondary);
}
.xl-et-toolbar__btn-lbl { flex: 1 }
.xl-et-toolbar__btn-bind {
  font: 500 10px/1 var(--xl-font-mono);
  color: var(--xl-color-text-muted);
}
.xl-et-toolbar__empty {
  font: 500 11px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text-muted);
  padding: 6px 8px;
}
`;

export default function EditorToolbar({ allowedFields }) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const items = useMemo(() => {
    if (!Array.isArray(allowedFields) || allowedFields.length === 0) {
      return DYNAMIC_FIELDS;
    }
    const allowed = {};
    for (let i = 0; i < allowedFields.length; i += 1) {
      allowed[allowedFields[i]] = true;
    }
    return DYNAMIC_FIELDS.filter((m) => allowed[m.key] === true);
  }, [allowedFields]);

  const onDragStart = (meta) => (e) => {
    try {
      const payload = JSON.stringify({
        type: meta.type,
        bindKey: meta.bindKey || null,
        wMm: meta.defaultWMm,
        hMm: meta.defaultHMm,
      });
      e.dataTransfer.setData(DND_MIME, payload);
      e.dataTransfer.setData('text/plain', meta.type);
      e.dataTransfer.effectAllowed = 'copy';
    } catch (_err) {
      // Ignorado.
    }
  };

  return (
    <div className="xl-et-toolbar" aria-label="Campos disponibles">
      <div className="xl-et-toolbar__hd">Campos</div>
      {items.length === 0 ? (
        <div className="xl-et-toolbar__empty">
          (Sin campos disponibles para esta plantilla)
        </div>
      ) : items.map((m) => (
        <div
          key={m.key}
          className="xl-et-toolbar__btn"
          draggable="true"
          onDragStart={onDragStart(m)}
          role="button"
          aria-label={'Agregar campo ' + m.label}
          title={m.bindKey || m.type}
        >
          <span className="xl-et-toolbar__btn-ico" aria-hidden="true">
            {m.glyph}
          </span>
          <span className="xl-et-toolbar__btn-lbl">{m.label}</span>
          {m.bindKey && (
            <span className="xl-et-toolbar__btn-bind">{m.bindKey}</span>
          )}
        </div>
      ))}
    </div>
  );
}
